import { Injectable } from '@angular/core';
import { ShortenPipe, StripTagsPipe, TrimPipe } from 'ngx-pipes';
import { BehaviorSubject } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { AbstractControl, FormGroup } from '@angular/forms';
import { HttpQuery } from './http-query';
import { RouterStateSnapshot } from '@angular/router';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ResponseDialogComponent } from '../../components/response-dialog/response-dialog.component';

export interface FileUploadTypeRequirement {
  fileExtensions: string[];
  accept: string;
}

export interface NormalizedFileItem {
  id: number | string;
  fileName: string;
  fileType?: string;
  path?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class HelpersService {
  constructor(
    private shorten: ShortenPipe,
    private strip: StripTagsPipe,
    private trim: TrimPipe,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {
    this.search$ = new BehaviorSubject('');
  }

  search$: BehaviorSubject<string>;
  private fileType: any = {
    docs: {
      fileExtensions: ['doc', 'docx', 'pdf'],
      accept: '.doc, .docx, .pdf',
    },
    images: {
      fileExtensions: ['png', 'jpg', 'jpeg'],
      accept: '.png, .jpg, .jpeg',
    },
  };

  generateParams(query: HttpQuery | any): HttpParams {
    const { page, limit, sortBy, sortDir, search, companyId, find, status } =
      query;

    let params = new HttpParams();

    if (page && page > -1) {
      params = params.append('page', page.toString());
    }
    if (limit && limit > -1) {
      params = params.append('limit', limit.toString());
    }
    if (sortBy) {
      params = params.append('sortBy', sortBy);
    }
    if (sortDir) {
      params = params.append('sortDir', sortDir);
    }
    if (search || search == '') {
      params = params.append('search', search);
    }
    if (companyId || companyId == '') {
      params = params.append('companyId', companyId);
    }
    if (find || find == '') {
      params = params.append('find', find);
    }
    if (status || status == '') {
      params = params.append('status', status);
    }
    return params;
  }

  shortIt(text: string): string {
    if (!text) {
      return '-';
    }
    return this.trim.transform(
      this.shorten.transform(this.strip.transform(text), 36, '...')
    );
  }

  toHttpParams(query: any, params: HttpParams, state: string): HttpParams {
    const attributes = Object.keys(query);
    if (attributes.length) {
      if (state == 'filter') {
        attributes.forEach((attribute) => {
          const keys = Object.keys(query[attribute]);
          if (keys.length) {
            keys.forEach((key) => {
              params = params.append(
                `${attribute}[${key}]`,
                query[attribute][key]
              );
            });
          }
        });
      } else {
        params = params.append(`search[${query.fields}]`, query.value);
      }
    }
    if (query.operation) {
      params = params.append('operation', query.operation);
    }
    return params;
  }

  parseSort(active: string, direction: string): string | undefined {
    if (active && direction) {
      return `${direction === 'asc' ? '' : '-'}${active}`;
    }
    return undefined;
  }

  sbError(message: string): any {
    return this.dialog
      .open(ResponseDialogComponent, {
        width: '700px',
        maxHeight: '95vh',
        data: {
          title: 'Failed',
          message: message,
          success: false,
        },
      })
      .afterClosed()
      .subscribe((item) => {});
  }

  sbSuccess(message: string): any {
    return this.dialog
      .open(ResponseDialogComponent, {
        width: '700px',
        maxHeight: '95vh',
        data: {
          title: 'Success',
          message: message,
          success: true,
        },
      })
      .afterClosed()
      .subscribe((item) => {});
  }

  isInvalid(form: FormGroup, control: string): boolean {
    return (
      (form.get(control)?.invalid &&
        (form.get(control)?.dirty || form.get(control)?.touched)) ||
      false
    );
  }

  formInvalid(formControl: AbstractControl) {
    return formControl.invalid && (formControl.dirty || formControl.touched);
  }

  previousUrl(state: RouterStateSnapshot) {
    const splited = state.url.split('/');
    return splited.splice(0, splited.length - 1).join('/');
  }

  openInNewTab(abs_path: string) {
    window.open(abs_path, '_blank');
  }

  dateFormat(date: Date) {
    return moment(date).format('DD-MM-YYYY');
  }

  dateTimeFormat(date: Date) {
    return moment(date).format('HH:mm:ss');
  }

  dateTimeFullFormat(date: Date) {
    return moment(date).format('DD-MM-YYYY HH:mm');
  }

  getFileType(type: string): FileUploadTypeRequirement {
    return this.fileType[type];
  }

  shortNumber(value: any) {
    const suffixes = ['', 'rb', 'jt', 'm', 't'];
    let newValue = value;
    let suffixNum = 0;

    while (newValue >= 1000) {
      newValue /= 1000;
      suffixNum++;
    }

    newValue = newValue.toPrecision(3);
    newValue += suffixes[suffixNum];

    return newValue;
  }

  parseImageUrl(relPath: string): string {
    return `${environment.host}${relPath}`;
  }

  parseAmount(amount: any) {
    if (amount == null) amount = 0;
    amount = (typeof amount === 'string' ? amount : String(amount)).replace(
      /,/g,
      ''
    );
    return parseFloat(amount);
  }

  normalizeFileItems(element: any): NormalizedFileItem[] {
    if (!element) return [];

    const candidates = [
      element,
      element.cashadvance,
      element.cashAdvance,
      element.cashAdvanceData,
      element.reimburse,
      element.reimburseData,
    ].filter(Boolean);

    const byId = new Map<number | string, NormalizedFileItem>();

    const upsert = (file: any, fallbackName?: string) => {
      if (!file?.id) return;
      const existing = byId.get(file.id) || ({} as NormalizedFileItem);
      byId.set(file.id, {
        ...existing,
        ...file,
        id: file.id,
        fileName:
          file.fileName || existing.fileName || fallbackName || `File #${file.id}`,
      });
    };

    candidates.forEach((item: any) => {
      if (Array.isArray(item.files)) {
        item.files.forEach((f: any, i: number) => upsert(f, `Attachment ${i + 1}`));
      }

      if (item.file?.id) {
        upsert(item.file, item.file.fileName || 'Attachment 1');
      }

      if (Array.isArray(item.fileIds)) {
        item.fileIds.forEach((id: any, i: number) => {
          const numericId = Number.isNaN(Number(id)) ? id : Number(id);
          upsert({ id: numericId }, `Attachment ${i + 1}`);
        });
      }

      [item.settlement, item.settlements, item.settlementData].forEach(
        (settlementList: any) => {
          if (!Array.isArray(settlementList)) return;
          settlementList.forEach((settlement: any, i: number) => {
            if (settlement?.file?.id) {
              upsert(
                settlement.file,
                settlement.file.fileName || `Settlement ${i + 1}`,
              );
            }
          });
        },
      );
    });

    return Array.from(byId.values());
  }

  resolveTargetFile(item: any, fallback?: any): NormalizedFileItem | null {
    const direct = item?.file?.id ? item.file : item;
    if (
      direct?.id &&
      (direct?.fileName || direct?.fileType || direct?.path || direct?.created_at)
    ) {
      return direct;
    }

    const fromItem = this.normalizeFileItems(item)[0];
    if (fromItem) return fromItem;

    const fromFallback = this.normalizeFileItems(fallback)[0];
    if (fromFallback) return fromFallback;

    return null;
  }

  canPreviewInline(file: any): boolean {
    const type = (file?.fileType || '').toLowerCase();
    const name = (file?.fileName || '').toLowerCase();
    const path = (file?.path || '').toLowerCase();

    if (type.includes('pdf') || type.startsWith('image/')) return true;
    if (name.endsWith('.pdf') || path.endsWith('.pdf')) return true;
    if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(name)) return true;
    if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(path)) return true;

    return false;
  }

  resolveBlobMimeType(file: any, blob?: Blob | null): string {
    const type = (file?.fileType || blob?.type || '').toLowerCase();
    const name = (file?.fileName || '').toLowerCase();
    const path = (file?.path || '').toLowerCase();

    if (type && type !== 'application/octet-stream') {
      return type;
    }
    if (name.endsWith('.pdf') || path.endsWith('.pdf')) {
      return 'application/pdf';
    }
    if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(name) || /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(path)) {
      return 'image/*';
    }

    return blob?.type || 'application/octet-stream';
  }

  openBlobInNewTab(blob: Blob, file?: any): void {
    const mimeType = this.resolveBlobMimeType(file, blob);
    const typedBlob =
      blob.type === mimeType ? blob : new Blob([blob], { type: mimeType });
    const url = URL.createObjectURL(typedBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
